import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { parseRankingRewards } from '@sorare/core/src/lib/rewards';

import {
  RewardRange,
  RewardRangeVariant,
} from '#src/components/leaderboard/RewardRange/index.tsx';
import { isFixtureOpened } from '#src/lib/so5.ts';

import { CumulativeRankReward } from './CumulativeRankReward/index.tsx';
import { RankRewardTitle } from './RankRewardTitle/index.tsx';
import { CumulativeRanking } from './Ranking/index.tsx';
import type {
  CumulativeRankRewardsQuery,
  CumulativeRankRewardsQueryVariables,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  padding-top: var(--intermediate-unit);
  padding-bottom: var(--intermediate-unit);
  &.isUpcoming {
    padding-top: var(--intermediate-unit);
    padding-bottom: var(--intermediate-unit);
  }
`;
const StyledBox = styled(Box)`
  background-color: var(--c-black);
`;

const CUMULATIVE_RANK_REWARDS_QUERY = gql`
  query CumulativeRankRewardsQuery($userGroupSlug: String!) {
    so5 {
      so5UserGroup(slug: $userGroupSlug) {
        slug
        membershipsCount
        startSo5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
        myMembership {
          id
          score
          ranking
          ...CumulativeRanking_So5UserGroupMembership
          ...CumulativeRankReward_So5UserGroupMembership
        }
        rewardsConfig {
          ranking {
            id
            fromUserRanking {
              id
              overallRanking
              overallScore
            }
            toUserRanking {
              id
              overallRanking
              overallScore
            }
            ...CumulativeRankReward_so5RewardConfig
            ...parseRankingRewards_so5RewardConfig
          }
        }
      }
    }
  }
  ${isFixtureOpened.fragments.so5Fixture}
  ${CumulativeRankReward.fragments.so5RewardConfig}
  ${parseRankingRewards.fragments.so5RewardConfig}
  ${CumulativeRanking.fragments.So5UserGroupMembership}
  ${CumulativeRankReward.fragments.So5UserGroupMembership}
` as TypedDocumentNode<
  CumulativeRankRewardsQuery,
  CumulativeRankRewardsQueryVariables
>;

type TotalPrizePoolWrapperProps = {
  usdTotalAmount: number;
  children?: ReactNode;
};
const TotalPrizePoolWrapper = ({
  usdTotalAmount,
  children,
}: TotalPrizePoolWrapperProps) => {
  return (
    <>
      <SBHorizontal gap={4}>
        <BodyM as="div">
          <FormattedMessage {...fantasy.prizePool} />
        </BodyM>
        <Horizontal>
          <AnyRewardConfig
            variant={Variant.INLINE}
            anyRewardConfig={{
              __typename: 'MonetaryRewardConfig',
              id: 'fake-id',
              monetaryAmount: {
                __typename: 'MonetaryAmount',
                referenceCurrency: SupportedCurrency.USD,
                usdCents: usdTotalAmount,
                eurCents: null,
                gbpCents: null,
                wei: null,
                lamport: null,
              },
            }}
          />
        </Horizontal>
      </SBHorizontal>
      <StyledBox>
        <Vertical gap={2}>{children}</Vertical>
      </StyledBox>
    </>
  );
};

type Props = {
  userGroupSlug: string;
  activeLineupId?: string;
  withTotalPrizePool?: boolean;
  withCollapsibleRanking?: boolean;
  doNotDisplayUsers?: boolean;
  rankingLink?: (
    fixtureSlug: string,
    userGroupSlug: string,
    userSlug: string
  ) => string;
};
export const CumulativeRankRewards = ({
  userGroupSlug,
  activeLineupId,
  withTotalPrizePool = false,
  withCollapsibleRanking = false,
  doNotDisplayUsers = false,
  rankingLink,
}: Props) => {
  const { exchangeRate } = useConfigContext();
  const { data } = useQuery(CUMULATIVE_RANK_REWARDS_QUERY, {
    variables: { userGroupSlug },
  });

  const userGroup = data?.so5?.so5UserGroup;

  if (!userGroup?.rewardsConfig?.ranking?.length) {
    return null;
  }

  const isUpcoming =
    userGroup.startSo5Fixture && isFixtureOpened(userGroup.startSo5Fixture);
  const parsedRankRewards = parseRankingRewards(
    userGroup?.rewardsConfig.ranking || []
  );

  const myRangeIndex = userGroup.myMembership
    ? parsedRankRewards.findIndex(
        rankReward =>
          (userGroup.myMembership?.ranking || 0) >=
            (rankReward.startRank ||
              rankReward.fromUserRanking?.overallRanking ||
              0) &&
          (userGroup.myMembership?.ranking || 0) <=
            (rankReward.endRank ||
              rankReward.toUserRanking?.overallRanking ||
              0)
      )
    : -1;

  const isMyRankingNotRewarded = myRangeIndex === -1;
  const lastRange = parsedRankRewards[parsedRankRewards.length - 1];

  const usdTotalAmount = parsedRankRewards
    .flatMap(rankReward => rankReward.rewardConfigs)
    .filter(isTypeFn('MonetaryRewardConfig'))
    .reduce((acc, rewardConfig) => {
      const usdAmount = MonetaryAmount.fromRates(
        rewardConfig.monetaryAmount,
        exchangeRate.ethRates
      ).usd;

      return acc + usdAmount;
    }, 0);

  return (
    <Wrapper gap={2} className={classNames({ isUpcoming })}>
      <ConditionalWrapper
        Wrapper={TotalPrizePoolWrapper}
        props={{ usdTotalAmount }}
        wrap={withTotalPrizePool}
      >
        {parsedRankRewards.map((rankReward, i) => {
          const isMyNextRange = myRangeIndex ? i === myRangeIndex - 1 : false;
          const isLastRange = i === parsedRankRewards.length - 1;

          return (
            <CumulativeRankReward
              so5RewardConfig={rankReward}
              so5Ranking={
                isType(userGroup.myMembership, 'So5UserGroupMembership')
                  ? userGroup.myMembership
                  : undefined
              }
              isMyRange={myRangeIndex === i}
              isMyNextRange={
                isMyNextRange || (isMyRankingNotRewarded && isLastRange)
              }
              userGroupSlug={userGroupSlug}
              rewardRangeVariant={RewardRangeVariant.BLOCK}
              key={rankReward.startRank || rankReward.startPct}
              activeLineupId={activeLineupId}
              withCollapsibleRanking={withCollapsibleRanking}
              rankingLink={rankingLink}
              scoreDistanceFromMyRanking={
                userGroup.myMembership
                  ? (userGroup.myMembership.score || 0) -
                    (i === -1 || i < myRangeIndex
                      ? rankReward.toUserRanking?.overallScore || 0
                      : rankReward.fromUserRanking?.overallScore || 0)
                  : undefined
              }
              doNotDisplayUsers={doNotDisplayUsers}
            />
          );
        })}
      </ConditionalWrapper>

      {!!isMyRankingNotRewarded &&
        isType(userGroup.myMembership, 'So5UserGroupMembership') && (
          <RewardRange
            variant={RewardRangeVariant.BLOCK}
            title={
              <RankRewardTitle
                startRank={lastRange.endRank}
                startPct={lastRange.endPct}
                endRank={userGroup.membershipsCount}
                endPct={null}
              />
            }
            rewards={[]}
          >
            {!doNotDisplayUsers && (
              <CumulativeRanking
                so5Ranking={userGroup.myMembership}
                userGroupSlug={userGroupSlug}
                activeLineupId={activeLineupId}
                isMyRanking
                rankingLink={rankingLink}
              />
            )}
          </RewardRange>
        )}
    </Wrapper>
  );
};
